import { Link } from 'react-router-dom';
import {
  useCostOfLiving,
  getDisplayCost,
  DEFAULT_DISPLAY_BUCKET,
  type HouseholdBucket,
} from '../cost-of-living';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/components/ui/cn';
import type { Country } from '@/data';
import { PropertyTaxDetail } from '@/features/property-tax';
import { CarOwnershipDetail, useCarOwnership, getCarOwnership, getCarTierColor } from '@/features/car-ownership';
import { buildWikiUrl, type CountryCode as WikiCountryCode } from '@/features/wiki/architecture';
import { checklistPath } from '@/features/next-steps/slugs';

const intMoney = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/**
 * Compact metrics row: monthly cost + top tax rate. Both wrapped in tooltips
 * because users repeatedly ask "per person? rent only? for the family?".
 *
 * Cost comes from cost_of_living.json for the household the reader's own quiz
 * answers imply, not from a single figure shown to everyone. It used to read
 * `metrics.cost.monthly_eur_family`, which was fabricated (all 31 values were
 * multiples of 100 and matched no source) and described a family of four to
 * every reader regardless of who they live with.
 *
 * The row renders nothing when the dataset has not loaded or does not cover
 * the country. That is deliberate: the old field's defect was that an unsourced
 * number is indistinguishable from a sourced one once it is on the page.
 *
 * Tax = country.metrics.tax.pit_top_rate_pct.
 */
export function CostTaxRow({
  country,
  t,
  bucket = DEFAULT_DISPLAY_BUCKET,
}: {
  country: Country;
  t: (key: string, opts?: Record<string, unknown>) => string;
  bucket?: HouseholdBucket;
}) {
  const { data: costFile } = useCostOfLiving();
  const display = getDisplayCost(costFile, country.code, bucket);
  const cost = display?.amount;
  const tax = country.metrics?.tax.pit_top_rate_pct;
  if (cost == null && tax == null) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] tabular-nums text-ink-700">
      {cost != null && (
        <Tooltip
          content={`${t('countries.metrics.costIn', {
            household: t(`countries.metrics.costHousehold.${display!.bucket}`),
            city: display!.city,
          })}. ${t('countries.metrics.costHelper')} ${t('quiz.cost.disclaimer')}`}
        >
          <span className="cursor-help border-b border-dotted border-ink-300">
            {t('countries.metrics.costShort', {
              amount: '€' + intMoney.format(cost),
            })}
          </span>
        </Tooltip>
      )}
      {tax != null && (
        <Tooltip content={t('countries.metrics.taxHelper')}>
          <span className="cursor-help border-b border-dotted border-ink-300">
            {t('countries.metrics.taxShort', { rate: tax })}
          </span>
        </Tooltip>
      )}
    </div>
  );
}
import type { CountryMatchResult, ScoringWeights } from '../types';
import { ColorBar } from './ColorBar';
import { regionLabel } from '@/lib/region-label';
import {
  DIM_COLORS,
  pickBlocker,
  scoreBand,
  topReasonsWithDim,
  type TFn,
} from '../derive';

/**
 * Compact one-line rank row used for results beyond top-5 and (later) for
 * the list-view of the countries page.
 */
export interface CountryRankRowProps {
  match: CountryMatchResult;
  country: Country | undefined;
  lang: 'en' | 'ru';
  rank: number;
  t: TFn;
}

export function CountryRankRow({ match, country, lang, rank, t }: CountryRankRowProps) {
  const name = country
    ? lang === 'ru'
      ? country.name_ru
      : country.name_en
    : match.countryCode;
  const percent = Math.round(match.score * 100);
  const blocker = pickBlocker(match, t);

  return (
    <li className="flex items-center gap-3 border-b border-ink-100 py-2.5 last:border-0">
      <span className="w-6 shrink-0 font-mono text-[11px] tabular-nums text-ink-400">
        {rank}
      </span>
      <span className="text-lg leading-none" aria-hidden="true">
        {country?.flag_emoji ?? ''}
      </span>
      <span className="min-w-0 flex-1">
        <span className="font-display text-sm font-medium italic text-ink-900">
          {name}
        </span>
        {blocker && (
          <span className="ml-2 font-mono text-[11px] text-ink-400">— {blocker}</span>
        )}
      </span>
      <span className="shrink-0 font-mono text-sm font-medium tabular-nums text-terra-600">
        {percent}%
      </span>
    </li>
  );
}

/**
 * Compact grid card for /countries — match-dominant header, mini ColorBar,
 * top reason, visa tags, primary CTA.
 *
 * Designed for the post-quiz catalogue. Unscored / excluded variants are
 * rendered by the caller (CountriesPage) since they don't need scoring data.
 */
export interface CountryScoreCardProps {
  match: CountryMatchResult;
  country: Country;
  lang: 'en' | 'ru';
  weights: ScoringWeights;
  rank?: number;
  /** Total number of evaluated countries — used in rank label "#{rank} of {total}" */
  total?: number;
  t: TFn;
  /** Optional override for the CTA target — used by quiz result with preset hash. */
  mapHref?: string;
}

export function CountryScoreCard({
  match,
  country,
  lang,
  weights,
  rank,
  total,
  t,
  mapHref,
}: CountryScoreCardProps) {
  const name = lang === 'ru' ? country.name_ru : country.name_en;
  const percent = Math.round(match.score * 100);
  const { data: carFile } = useCarOwnership();
  const carEntry = getCarOwnership(carFile, country.code);
  const band = scoreBand(match.score);
  const reasons = topReasonsWithDim(match, weights, t, 1);
  const topReason = reasons[0];
  const hasMap = !!(country.data_available && country.map_url);
  // Primary link defaults to the country wiki hub — every country has one now,
  // and the hub itself surfaces the interactive map link for Italy.
  const hubHref = buildWikiUrl({ cc: country.code as WikiCountryCode, lang });
  // "Open Map" must go to the map URL, not the hub.
  const href = mapHref ?? (hasMap ? country.map_url! : hubHref);

  return (
    <article
      className="flex flex-col rounded-lg border border-ink-100 bg-paper-2 p-5 shadow-sm transition-shadow hover:shadow-md"
      data-testid={`country-card-${country.code}`}
    >
      <header className="flex items-start justify-between gap-3">
        <span className="text-3xl leading-none" aria-hidden="true">
          {country.flag_emoji}
        </span>
        <div className="flex flex-col items-end leading-none">
          <span
            className="font-display text-3xl font-medium tabular-nums text-terra-600"
            aria-label={t('quiz.result.matchPercent', { percent })}
          >
            {percent}%
          </span>
          <span className="mt-1 font-mono text-[10px] uppercase tracking-wider text-ink-500">
            {t(`countries.scoreBand.${band}`, { defaultValue: band })}
          </span>
        </div>
      </header>

      {rank != null && (
        <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-ink-400">
          {t('quiz.result.rank', { rank, total, defaultValue: `#${rank}` })}
        </p>
      )}

      <h3 className="mt-3 font-display text-xl font-medium italic tracking-tight text-ink-900">
        {name}
      </h3>
      <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-ink-500">
        {country.code} · {regionLabel(country.region, t)}
      </p>

      <div className="mt-4">
        <ColorBar
          components={match.components}
          weights={weights}
          t={t}
          size="sm"
        />
      </div>

      {topReason && (
        <p className="mt-3 flex items-start gap-2 text-sm leading-snug text-ink-700">
          <span
            aria-hidden="true"
            className={cn('mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full', DIM_COLORS[topReason.dim])}
          />
          <span>{topReason.text}</span>
        </p>
      )}

      <CostTaxRow country={country} t={t} />

      {country.visa_programs.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {country.visa_programs.slice(0, 3).map((p) => (
            <span
              key={p}
              className="rounded-full border border-ink-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-500"
            >
              {p}
            </span>
          ))}
          {country.visa_programs.length > 3 && (
            <Tooltip content={country.visa_programs.slice(3).join(', ')}>
              <span className="rounded-full border border-ink-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-500">
                +{country.visa_programs.length - 3}
              </span>
            </Tooltip>
          )}
        </div>
      )}

      {/* "Owning a home here" — collapsible second-tax-queue view. Closed
          by default so the card stays scannable; the chip on QuizResultPage
          gives the headline at-a-glance, this gives the full breakdown for
          users who want to drill in. */}
      <details
        className="mt-4 rounded-md border border-ink-100 bg-paper open:bg-paper-3"
        data-testid={`country-owning-${country.code}`}
      >
        <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-700 hover:text-ink-900">
          {t('propertyTax.summaryTitle', { defaultValue: 'Owning a home here' })}
          <span aria-hidden="true">▾</span>
        </summary>
        <div className="border-t border-ink-100 p-2">
          <PropertyTaxDetail countryCode={country.code} compact />
        </div>
      </details>

      {/* Car ownership costs — shown only when data is loaded */}
      {carEntry && (
        <details
          className="mt-2 rounded-md border border-ink-100 bg-paper open:bg-paper-3"
          data-testid={`country-car-${country.code}`}
        >
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-700 hover:text-ink-900">
            <span className="flex items-center gap-1.5">
              🚗
              <span>{t('carOwnership.summaryTitle', { defaultValue: 'Car ownership' })}</span>
              {carEntry.confidence !== 'low' && carEntry.monthly_total_est_eur != null && (
                <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${getCarTierColor(carEntry.tier)}`}>
                  {t('carOwnership.chipMonthly', {
                    amount: carEntry.monthly_total_est_eur,
                    defaultValue: `~€${carEntry.monthly_total_est_eur}/mo`,
                  })}
                </span>
              )}
            </span>
            <span aria-hidden="true">▾</span>
          </summary>
          <div className="border-t border-ink-100 p-2">
            <CarOwnershipDetail entry={carEntry} countryCode={country.code} lang={lang} />
          </div>
        </details>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 pt-4">
        {/* Primary CTA — opens the encyclopedia hub for every country.
            The hub itself surfaces the interactive Italy map when relevant. */}
        <Link
          to={hubHref}
          className="font-mono text-xs font-semibold uppercase tracking-wider text-terra-600 hover:underline"
          data-testid={`country-card-cta-guide-${country.code}`}
        >
          {t('countries.readGuide', { defaultValue: 'Read the guide →' })}
        </Link>
        {/* Moving checklist — every country in the roster ships a per-
            country checklist surface at /checklists/<slug>, so this CTA
            is universal and not gated on the wiki being published. Kept
            at secondary weight so the primary surface stays the guide. */}
        <Link
          to={checklistPath(country.code)}
          className="font-mono text-xs uppercase tracking-wider text-ink-500 hover:text-ink-900"
          data-testid={`country-card-cta-checklist-${country.code}`}
        >
          {t('countries.checklist', { defaultValue: 'Checklist →' })}
        </Link>
        {/* Tertiary CTA — interactive map, when the country has one
            (currently only Italy). Kept compact + lower-weight so the
            guide remains the primary affordance. */}
        {hasMap && (
          <Link
            to={href}
            className="font-mono text-xs uppercase tracking-wider text-ink-500 hover:text-ink-900"
            data-testid={`country-card-cta-${country.code}`}
          >
            {t('countries.openMap')}
          </Link>
        )}
      </div>
    </article>
  );
}
